import {
  Instance,
  SnapshotIn,
  getRoot,
  types
} from "mobx-state-tree";

import { IRoot } from "./Root";
import { LengthUnits } from "convert-units/definitions/length";
import { VolumeFlowRateUnits } from "convert-units/definitions/volumeFlowRate";
import { isLengthUnit, isVolumeFlowRateUnit, normalizeFlowRateUnit } from "../convert";
import SensorBase from "./SensorBase";
import { ISensor } from "./Sensors";

const {
  compose,
  maybeNull,
  frozen,
  literal,
  map,
  model,
  number,
  string,
  snapshotProcessor,
} = types;


export const Sample = model({
  time: string,
  value: number,
});

export interface ISample_ extends SnapshotIn<typeof Sample> { }

export const StageData = model({
  unit: frozen<LengthUnits>(),
  latest: maybeNull(Sample),
  max: map(Sample),
}).actions((self) => ({
  afterCreate() {
    if (!isLengthUnit(self.unit)) {
      throw new Error(`Unhandled unit ${self.unit}`);
    }
  }
})).named("StageData");


export interface IStageData_ extends SnapshotIn<typeof StageData> { }

export const DischargeDataModel = model({
  unit: frozen<VolumeFlowRateUnits>(),
  latest: maybeNull(Sample),
  max: map(Sample),
}).named("DischargeData");

export const DischargeData = snapshotProcessor(DischargeDataModel, {
  preProcessor(snapshot) {
    if (!isVolumeFlowRateUnit(snapshot.unit)) {
      return {
        ...snapshot,
        unit: normalizeFlowRateUnit(snapshot.unit),
      }
    }
    return snapshot;
  }
});

export interface IDischargeData_ extends SnapshotIn<typeof DischargeData> { }

export const StreamSensor = compose(
  SensorBase,
  model({
    type: literal("stream"),
    stage: maybeNull(StageData),
    discharge: maybeNull(DischargeData),
  }))
  .views((self) => ({
    get hasDischarge() {
      return self.discharge
    },

    get latestDate() {
      const root = getRoot<IRoot>(self);
      const measure = root.sensors.selected?.display.measure;
      switch (measure) {
        case "stage":
          const stage = self.stage;
          return stage?.latest ? new Date(stage.latest.time) : null
        case "discharge":
          const discharge = self.discharge;
          return discharge?.latest ? new Date(discharge.latest.time): null
        default:
          throw new Error(`Unhandled measure ${measure}`);
      }
    },
    stageFor(period: string) {
      if (!self.stage) {
        return undefined;
      }

      const stage = self.stage;
      const sample = (period === "last") ? stage.latest : stage.max.get(period)
      return sample
    },
    dischargeFor(period: string) {
      if (!self.discharge) {
        return undefined;
      }

      const discharge = self.discharge
      const sample = (period === "last") ? discharge.latest : discharge.max.get(period)

      return sample

    },
  }))
  .named("StreamSensor");

export const isStreamSensor = (sensor: ISensor | undefined): sensor is IStreamSensor => sensor?.type === "stream";
export interface IStreamSensor extends Instance<typeof StreamSensor> { }
export interface StreamSensor_ extends SnapshotIn<typeof StreamSensor> { }
